import { ConditionV, RuleGroupV, TriggersV } from "@/types/conditions";
import { DynamicFieldV } from "@/types/dynamic-data";
import * as t from "io-ts";
import { EnvironmentV } from "@/types/load";
import {
  BREAKDANCE_BLOCK_POST_TYPE,
  BREAKDANCE_FOOTER_POST_TYPE,
  BREAKDANCE_HEADER_POST_TYPE,
  BREAKDANCE_TEMPLATE_POST_TYPE,
  OXYGEN_BLOCK_POST_TYPE,
  OXYGEN_FOOTER_POST_TYPE,
  OXYGEN_HEADER_POST_TYPE,
  OXYGEN_TEMPLATE_POST_TYPE
} from "@/lib/constants/post-types";

export const TemplateTypeV = t.intersection([
  t.type({
    slug: t.string,
    label: t.string
  }),
  t.partial({
    defaultPriority: t.number
  })
]);

export const TemplateParentsV = t.array(
  t.type({
    text: t.string,
    value: t.number
  })
);

export type TemplateParents = t.TypeOf<typeof TemplateParentsV>;

export const TemplateTypeCategoriesV = t.array(
  t.type({
    label: t.string,
    types: t.array(TemplateTypeV)
  })
);

export type TemplateTypeCategories = t.TypeOf<typeof TemplateTypeCategoriesV>;

export const TemplateSettingsV = t.partial({
  parentId: t.number,
  type: t.string,
  ruleGroups: t.array(RuleGroupV),
  triggers: TriggersV,
  priority: t.number,
  disabled: t.boolean,
  fallback: t.boolean,
  onlyShowOnce: t.boolean,
  avoidMultiple: t.boolean,
  limitPageLoad: t.union([t.number, t.null]),
  limitSession: t.union([t.number, t.null]),
  limitForever: t.union([t.number, t.null])
});

export type TemplateSettings = t.TypeOf<typeof TemplateSettingsV>;

export const SupportedPostTypeV = t.union([
  t.literal(BREAKDANCE_TEMPLATE_POST_TYPE),
  t.literal(OXYGEN_TEMPLATE_POST_TYPE),
  t.literal(BREAKDANCE_HEADER_POST_TYPE),
  t.literal(OXYGEN_HEADER_POST_TYPE),
  t.literal(BREAKDANCE_FOOTER_POST_TYPE),
  t.literal(OXYGEN_FOOTER_POST_TYPE),
  t.literal(BREAKDANCE_BLOCK_POST_TYPE),
  t.literal(OXYGEN_BLOCK_POST_TYPE),
  t.literal("breakdance_popup"),
  t.literal("breakdance_acf_block")
]);

export type SupportedPostType = t.TypeOf<typeof SupportedPostTypeV>;

export const PartialTemplateV = t.type({
  id: t.number,
  title: t.string,
  editInBreakdanceLink: t.string,
  postType: SupportedPostTypeV,
  status: t.union([t.literal("publish"), t.literal("trash"), t.undefined])
});

export const TemplateV = t.intersection([
  PartialTemplateV,
  t.type({
    settings: t.union([TemplateSettingsV, t.null])
  })
]);

export type Template = t.TypeOf<typeof TemplateV>;

export const TemplateWithValidSettingsV = t.intersection([
  PartialTemplateV,
  t.type({
    settings: TemplateSettingsV
  })
]);

export type TemplateWithValidSettings = t.TypeOf<
  typeof TemplateWithValidSettingsV
>;

const PopupTriggersV = t.type({
  text: t.string,
  value: t.string,
  proOnly: t.union([t.undefined, t.boolean])
});

export type PopupTriggers = t.TypeOf<typeof PopupTriggersV>;

export const TemplatesManagementDataV = t.type({
  conditions: t.array(ConditionV),
  dynamicFields: t.array(DynamicFieldV),
  templateTypeCategories: TemplateTypeCategoriesV,
  popupTriggers: t.union([t.null, t.array(PopupTriggersV)])
});

export type TemplatesManagementData = t.TypeOf<typeof TemplatesManagementDataV>;

export const ManageTemplatesDataToLoadV = t.type({
  templates: t.array(TemplateV),
  templatesManagementData: TemplatesManagementDataV,
  isWooCommerceActive: t.boolean,
  environment: EnvironmentV
});

export type ManageTemplatesDataToLoad = t.TypeOf<
  typeof ManageTemplatesDataToLoadV
>;

export type QuickAddTemplate = {
  name: string;
  textColor: string;
  backgroundColor: string;
  category: string;
  settings: TemplateSettings;
  openDialog?: boolean;
};

export type TemplateStatus = "publish" | "trash";

export type EmptyStateV = {
  itemsByStatus: number;
  filteredItemsBySearch: number;
  templateName:
    | "Template"
    | "Footer"
    | "Header"
    | "Unsupported template"
    | "Popup"
    | "Global Block";
  showTrash: boolean;
};
